import styled, { keyframes } from 'styled-components';
import { DX } from '../../../theme/v2';
import { Canvas, Caption, Rail, RailItem, StatusBar, Toolbar } from '../../../components/v2';
import { BreadcrumbContainer } from '../../../components/Breadcrumb/style';

/* A group label, in the voice of a sentence rather than of a label.
 *
 * 11/700 uppercase with half a pixel of tracking is what a design system reaches for when it wants
 * a caption to be unmistakably a caption — and on a rail of six rows it shouts three times at
 * things nobody needs told twice. Same information, said quietly: normal case, one size down from
 * the rows it heads, and the grey that means "this is not the thing you are looking for". */
export const groupLabel = `
  text-transform: none;
  letter-spacing: normal;
  font-size: ${DX.fz.xs};
  font-weight: ${DX.fw.regular};
  line-height: ${DX.lh.xs};
  color: ${DX.textFaint};
`;

/**
 * The rail, as a menu.
 *
 * A glyph in front of every row: the views are read a hundred times a day and after the first week
 * nobody reads them at all — they aim at the shape. The count stays on the right, because "how many
 * are there" is the other half of what a view is.
 */
export const ListRail = styled(Rail)`
  display: flex;
  flex-direction: column;
  padding: ${DX.space[5]} ${DX.space[4]};

  ${Caption} {
    ${groupLabel}
    padding: 0 ${DX.space[3]} ${DX.space[2]};
  }

  /* A group is a group because of the space above it — so the space between two of them is worth
     more than the space between two rows inside one. */
  ${Caption}:not(:first-child) {
    padding-top: ${DX.space[7]};
  }

  /* Settings, integrations and the audit trail sit at the foot of the rail rather than in the
     avatar menu at the top right, which is where you go to sign out — three pages of the product
     were hidden behind a portrait. Bottom of the list, because they are the things you set up once
     and then rarely open. */
  .foot {
    margin-top: auto;
    padding-top: ${DX.space[5]};
  }

  ${RailItem} {
    justify-content: flex-start;
    gap: ${DX.space[3]};
    height: 30px;
    padding: 0 ${DX.space[3]};
  }

  /* Four pixels between two rows of a group: enough that the fill on the active one reads as a
     shape rather than as a band, and not so much that the group stops being one. */
  ${RailItem} + ${RailItem} {
    margin-top: ${DX.space[1]};
  }

  ${RailItem} .glyph {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 16px;
    color: ${DX.textFaint};
  }

  ${RailItem}[data-active='true'] .glyph {
    color: ${DX.text};
  }

  ${RailItem} .label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The count is a footnote to the view, not a second label: one size down, a shade lighter, and
     lined up on its digits so 9, 21 and 333 end in the same place. */
  ${RailItem} .count {
    margin-left: auto;
    font-size: ${DX.fz.xs};
    font-variant-numeric: tabular-nums;
    color: ${DX.textFaint};
  }
`;

/* The project list and the periods page said the same thing twice: one named a project, the other
   showed where each of its periods had got to. Here the project's row opens onto that table in
   place, and the page around it wears the same chrome as every other V2 screen.

   Every value below comes from the token file — see docs/V2_DESIGN_REFERENCE.md for where those
   values were read off. The page's root carries `dx-v2`, or none of them resolve. */

/* One track per step, however many steps this report type has. */
const TRACKS = `minmax(88px, 116px) repeat(var(--steps, 8), minmax(88px, 1fr))`;

export const PeriodGrid = styled.div`
  display: flex;
  flex-direction: column;

  /* Sentence case, not uppercase: this table sits inside a row of ordinary sentences, and shouting
     nine column names at it made the card louder than the list. */
  .head {
    display: grid;
    grid-template-columns: ${TRACKS};
    gap: ${DX.space[4]};
    /* The negative margin is what keeps the columns aligned with the rows: the fill is 12px wider on
       each side, the padding gives it back, and the content box is the width it was. */
    margin: 0 calc(-1 * ${DX.space[4]});
    padding: ${DX.space[2]} ${DX.space[4]};
    border-radius: ${DX.radius.item};
    background: ${DX.surfaceSunken};
    font-size: ${DX.fz.caption};
    font-weight: ${DX.fw.strong};
    line-height: ${DX.lh.caption};
    color: ${DX.textMuted};
  }

  /* Four or five years of quarters is an ordinary project. Past about seven lines the periods
     scroll inside the row rather than push the rest of the list off the screen. */
  .rows {
    max-height: 296px;
    overflow-y: auto;
  }
`;

export const PeriodRow = styled.div`
  display: grid;
  grid-template-columns: ${TRACKS};
  gap: ${DX.space[4]};
  align-items: center;
  min-height: 36px;
  padding: ${DX.space[1]} 0;
  border-bottom: ${DX.hairlineSoft};

  &:last-child {
    border-bottom: none;
  }

  /* Filed periods keep their links — you can still open what was submitted — but not the colour
     that says there is something to do. */
  /* The element selector is deliberate: StepCell sets its own tone colour with the same specificity
     and is defined after this, so a plain attribute selector loses the tie. */
  &[data-history='true'] div[data-tone='next'] {
    color: ${DX.textMuted};
  }
`;

/**
 * One step of one period.
 *
 * Type carries the state and colour is spent twice: on the one step that is next, and on a step
 * that needs attention. A step that is done is the same black as everything else on the page, and a
 * step still out of reach is a dash.
 */
export const StepCell = styled.div`
  min-width: 0;
  font-size: ${DX.fz.sm};
  line-height: ${DX.lh.sm};
  color: ${DX.text};

  button,
  span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* A button does not inherit type. */
    font-size: ${DX.fz.sm};
    line-height: ${DX.lh.sm};
    color: inherit;
  }

  button {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &[data-tone='next'],
  &[data-tone='comment'] {
    color: ${DX.tone.next};
  }

  &[data-tone='attention'] {
    color: ${DX.tone.attention};
  }

  &[data-tone='muted'] {
    color: ${DX.tone.running};
  }

  &[data-tone='idle'] {
    color: ${DX.tone.idle};
  }

  /* A cell you can change looks like a control rather than like a word: hairline, chevron, and the
     two states in the colours this app has always given them. */
  button.pick {
    display: inline-flex;
    align-items: center;
    gap: ${DX.space[2]};
    padding: 1px ${DX.space[2]} 1px ${DX.space[3]};
    border: ${DX.hairline};
    border-radius: ${DX.radius.control};
    font-size: ${DX.fz.xs};
    color: ${DX.text};

    &:hover {
      background: ${DX.hover};
      text-decoration: none;
    }

    /* The state is the dot; the words stay the same black as the rest of the row, so a column of
       these reads as a column rather than as a warning. */
    .mark {
      flex-shrink: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${DX.tone.warning};
    }

    &[data-state='submitted'] .mark {
      background: ${DX.tone.ok};
    }

    svg {
      color: ${DX.textFaint};
    }
  }

  &.period {
    display: grid;
    grid-template-columns: 6px minmax(0, 1fr);
    align-items: center;
    gap: ${DX.space[2]};
    font-size: ${DX.fz.xs};
    font-weight: ${DX.fw.strong};
  }

  /* PF-UPDATING and its kind are as long as the period is short; on its own line it stops clipping
     the date it belongs to. */
  &.period .filing {
    grid-column: 2;
    font-size: ${DX.fz.caption};
    font-weight: ${DX.fw.regular};
    line-height: ${DX.lh.caption};
    color: ${DX.textMuted};
  }

  /* The tone belongs to the dot here, not to the period's name: a blue heading would say the date
     itself is the thing to click. */
  &.period[data-tone] {
    color: ${DX.text};
  }

  /* The same dot the card carries, so the row and the line above it are visibly the same thing. */
  &.period .dot {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${DX.textFaint};
  }

  &.period[data-tone='attention'] .dot {
    background: ${DX.tone.attention};
  }

  &.period[data-tone='comment'] .dot {
    background: ${DX.accent};
  }

  .filing {
    font-size: ${DX.fz.caption};
    font-weight: ${DX.fw.regular};
    color: ${DX.textMuted};
  }
`;

/**
 * The list, in the canvas.
 *
 * The page shape itself now comes from components/v2 — this is only what the shared table needs to
 * look like V2 while it is still the shared table: no border of its own, because the page frames it,
 * and a column caption that is a caption rather than a heading.
 */
export const ListCanvas = styled(Canvas)`
  table {
    border: none;
  }

  thead th {
    padding: ${DX.space[2]} ${DX.space[5]};
    ${DX.caption}
  }

  tbody td {
    padding: ${DX.space[3]} ${DX.space[5]};
  }
`;

/**
 * The toolbar's controls, in one register.
 *
 * Everything here is the shared ControlButton now; what is left to align is Mantine's own two — the
 * search field and the single filled action — onto the same height, type and hairline.
 */
export const ListToolbar = styled(Toolbar)`
  /* No rule under it. The bar is not a separate object sitting on top of the list — it is the top
     of the same page, and a line across the screen said otherwise twice a second. */
  border-bottom: none;
  /* On a narrow screen the controls wrap onto a second line instead of running off the edge. */
  flex-wrap: wrap;
  row-gap: ${DX.space[2]};
  padding-top: ${DX.space[2]};
  padding-bottom: ${DX.space[2]};

  /* A control that is only its glyph, and only the glyph: no outline, because a box around an icon
     is a second thing to look at and the icon was the point. It gets its own shape under the cursor
     and its colour when it is doing something. */
  .icon-btn {
    width: ${DX.control.base};
    padding: 0;
    justify-content: center;
    border-color: transparent;
    background: none;
  }

  .icon-btn svg {
    color: ${DX.textMuted};
  }

  .icon-btn:hover {
    background: ${DX.hover};
  }

  .icon-btn:hover svg {
    color: ${DX.text};
  }

  .icon-btn[data-active='true'] {
    border-color: transparent;
  }

  .icon-btn[data-active='true'] svg {
    color: ${DX.accent};
  }

  .mantine-Input-input {
    height: ${DX.control.base};
    min-height: ${DX.control.base};
    border-radius: ${DX.radius.control};
    border-color: ${DX.border};
    font-size: ${DX.fz.sm};
    font-weight: ${DX.fw.regular};
    color: ${DX.text};
  }

  .mantine-Input-input::placeholder {
    color: ${DX.textFaint};
  }

  .mantine-Button-root {
    height: ${DX.control.base};
    min-height: ${DX.control.base};
    border-radius: ${DX.radius.control};
    font-size: ${DX.fz.sm};
    font-weight: ${DX.fw.strong};
  }
`;

/* Room on the right for the floating design switch. */
/* The foot of the list: which twenty of the five hundred you are looking at, and the way to the
   next twenty. It used to hold 220px of empty padding on the right, to clear a floating design
   toggle that has since moved into the profile menu — the pages had been sitting a hand's width in
   from the edge ever since. */
export const ListStatusBar = styled(StatusBar)`
  .range {
    font-variant-numeric: tabular-nums;
  }

  /* Mantine's pagination arrives as a row of bordered squares with a blue one among them — the
     legacy language, in a page built out of hairlines and one accent. Same control, said quietly:
     no boxes, the current page on the fill the rail uses for the row you are on. */
  .mantine-Pagination-control {
    height: 24px;
    min-width: 24px;
    padding: 0 ${DX.space[2]};
    border: none;
    border-radius: ${DX.radius.control};
    background: transparent;
    color: ${DX.textMuted};
    font-size: ${DX.fz.xs};
    font-weight: ${DX.fw.regular};
    font-variant-numeric: tabular-nums;
  }

  .mantine-Pagination-control:hover {
    background: ${DX.hover};
    color: ${DX.text};
  }

  .mantine-Pagination-control[data-active] {
    background: ${DX.selected};
    color: ${DX.text};
    font-weight: ${DX.fw.medium};
  }

  .mantine-Pagination-dots {
    color: ${DX.textFaint};
  }
`;

/* The chevron that opens a project, in the first cell of its row. */
export const ExpandToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: ${DX.space[2]};
  border: none;
  border-radius: ${DX.radius.control};
  background: transparent;
  color: ${DX.textMuted};
  cursor: pointer;
  transition: transform 120ms ${DX.ease};

  &:hover {
    background: ${DX.active};
  }

  &[data-open='true'] {
    transform: rotate(90deg);
  }
`;

/**
 * A project, as a card rather than a row.
 *
 * A row could carry a name and four dates; anything about where the project had got to lived on
 * another screen. The card has room for the periods themselves, so the second screen is only needed
 * when a tile is not enough — and then the card opens onto the same table underneath.
 */
export const Card = styled.article`
  /* Not a card any more, and not a table either — the same row the queue is made of. Sixty outlined
     boxes were sixty frames drawn around one sentence each; what a row needs is space around it and
     a fill when the cursor is on it. */
  border-radius: ${DX.radius.item};

  header {
    display: flex;
    align-items: center;
    gap: ${DX.space[5]};
    min-height: 44px;
    padding: ${DX.space[2]} ${DX.space[3]};
    border-radius: ${DX.radius.item};
    cursor: pointer;
    transition: background-color ${DX.duration} ${DX.ease};
  }

  /* The fill belongs to the row, not to the table it opens: an expanded project would otherwise
     light up eight periods deep. */
  header:hover {
    background: ${DX.hover};
  }

  &[data-selected='true'] > header {
    background: ${DX.selected};
  }

  &[data-selecting='true'] .comments,
  &[data-selecting='true'] .assignee {
    pointer-events: none;
  }

  &[data-selecting='true'] .actions {
    visibility: hidden;
  }

  &[data-locked='true'] {
    cursor: not-allowed;
  }

  &[data-locked='true'] > header {
    opacity: 0.4;
    pointer-events: none;
  }

  /* An open row stays lit while its periods are showing — otherwise the table below belongs to
     nothing in particular. */
  &[data-expanded='true'] > header {
    background: ${DX.hover};
  }

  /* The whole row opens, so it is focusable — but a mouse click should not leave a ring around it.
     Keyboard focus still gets one, thin: it marks where you are, it is not an alarm. */
  header:focus {
    outline: none;
  }

  header:focus-visible {
    outline: 1px solid ${DX.accent};
    outline-offset: -1px;
  }

  /* Said beside the name even when the thread is on a period that was filed: it is the one thing in
     a project's history that is still waiting on a person. Blue while nothing has been resolved on
     it, grey once the conversation has been read and worked on. */
  .comments {
    display: inline-flex;
    align-items: center;
    gap: ${DX.space[1]};
    padding: ${DX.space[1]} ${DX.space[2]};
    border: none;
    border-radius: ${DX.radius.control};
    background: none;
    font-size: ${DX.fz.caption};
    color: ${DX.textMuted};
    cursor: pointer;

    &[data-new='true'] {
      color: ${DX.accent};
    }

    &:hover {
      background: ${DX.hover};
    }
  }

  /* The name takes what is left. Every other column is now as wide as the longest thing that goes
     in it and no wider, so "what is left" is most of the row — which is right: the name is the one
     column whose content nobody can predict. */
  .who {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .who .line {
    display: flex;
    align-items: baseline;
    gap: ${DX.space[3]};
    min-width: 0;
  }

  /* A group says what it is with its glyph and its count, on the name's own line — a second line
     for it made those rows six pixels taller than every other one. */
  .kind {
    flex-shrink: 0;
    color: ${DX.textFaint};
  }

  /* Regular weight, one size up. The name leads its row because it is black, a shade larger, and
     everything beside it is grey — that is enough, and it does not turn a page of sixty into a page
     of sixty headings. */
  .name {
    max-width: 100%;
    padding: 0;
    border: none;
    background: none;
    font-size: ${DX.fz.md};
    font-weight: ${DX.fw.regular};
    line-height: ${DX.lh.md};
    color: ${DX.text};
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  /* The report type: a word rather than a box — it is not a state and not a control — but a word in
     a column of its own. Which report a project is answers half the questions asked of this page,
     and set against the name it moved with every name's length and could not be read down. */
  .meta {
    flex: 0 0 88px;
    min-width: 0;
    font-size: ${DX.fz.sm};
    line-height: ${DX.lh.sm};
    color: ${DX.textMuted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Next due: a fixed column, so the dates line up down the list. */
  .due {
    flex: 0 0 270px;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: ${DX.space[2]};
    white-space: nowrap;
    overflow: hidden;

    em {
      font-style: normal;
      font-size: ${DX.fz.xs};
      color: ${DX.textFaint};
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  /* One line: what changed last. "Updated May 22, 2026" is the longest it gets. */
  .dates {
    /* Two dates now, not one — wide enough for "Updated 12 Aug 2026 · Created 4 Jul 2026" without
       the row growing a second storey. */
    flex: 0 0 270px;
    font-size: ${DX.fz.xs};
    line-height: ${DX.lh.xs};
    color: ${DX.text7};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    /* The separator sits between two facts of equal weight, so it recedes rather than divides. */
    .sep {
      color: ${DX.textFaint};
    }
  }

  /* A column of its own, so the assignee lines up down the list rather than sitting at whatever
     width each name happens to end at. */
  /* The assignee column is an editable control and stands 33px tall on its own, which made a
     project's card seventeen pixels taller than a group's. Fixed to the line it sits on; the control
     still opens on click. */
  .assignee {
    flex: 0 0 148px;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: ${DX.space[2]};
    height: 20px;
    /* The assignee column brings its own markup and no type; the size is set here and inherited. */
    font-size: ${DX.fz.sm};
    line-height: ${DX.lh.sm};
    color: ${DX.textMuted};
  }

  .assignee > * {
    display: flex;
    align-items: center;
    height: 100%;
  }

  /* One line. "Farrukh Mukhitdinov" wrapped inside its 148px and made that row two storeys tall
     while every other row stayed one — a column of names cannot set the height of the list. */
  .assignee * {
    font-size: inherit;
    line-height: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* An admin's assignee is a quiet button; its chevron shows on the row being pointed at. */
  .assignee .assignee-btn {
    display: inline-flex;
    align-items: center;
    gap: ${DX.space[1]};
    max-width: 100%;
    margin-left: calc(-1 * ${DX.space[1]});
    padding: 0 ${DX.space[1]};
    border: none;
    border-radius: ${DX.radius.control};
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
    transition:
      background-color ${DX.duration} ${DX.ease},
      color ${DX.duration} ${DX.ease};

    &:hover,
    &[aria-expanded='true'] {
      background: ${DX.active};
      color: ${DX.text};
    }
  }

  .assignee .assignee-chevron {
    flex: none;
    opacity: 0;
    transition: opacity ${DX.duration} ${DX.ease};
  }

  header:hover .assignee-chevron,
  .assignee-btn:focus-visible .assignee-chevron,
  .assignee-btn[aria-expanded='true'] .assignee-chevron {
    opacity: 1;
  }

  /* The row's own controls: quiet until the row is pointed at, because they are not what the row
     is for. */
  /* Fixed width, or the middle columns shift between a project's card and a group's. */
  .actions {
    flex: 0 0 66px;
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${DX.space[2]};

    /* The dots appear on the row you are pointing at; opacity does the hiding. */
    .more {
      opacity: 0;
      transition:
        opacity ${DX.duration} ${DX.ease},
        background-color ${DX.duration} ${DX.ease};
    }

    .more {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      padding: 0;
      border: none;
      border-radius: ${DX.radius.control};
      background: none;
      color: ${DX.textMuted};
      cursor: pointer;

      &:hover {
        background: ${DX.active};
        color: ${DX.text};
      }
    }

    /* The chevron is always there: it is the affordance that says the row opens. */
    .toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      padding: 0;
      border: none;
      border-radius: ${DX.radius.control};
      background: none;
      color: ${DX.textMuted};
      cursor: pointer;
      transition: transform 120ms ${DX.ease};
    }

    .toggle[data-open='true'] {
      transform: rotate(90deg);
    }
  }

  &:hover .actions .more,
  &:focus-within .actions .more {
    opacity: 1;
  }

  /* Inside the card, not a second card: the grey strip of column names is the separation, so there
     is no rule above it doing the same job twice.

     It opens by growing its own row from 0fr to 1fr — the one way to animate to a height nobody can
     know in advance, and the reason the padding lives on the child rather than here. */
  .expansion {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 180ms ${DX.ease};
  }

  .expansion[data-open='true'] {
    grid-template-rows: 1fr;
  }

  /* The clipping box carries no padding of its own: with border-box, padding on a zero-height row
     still renders, which left a grey sliver of the table's head under every collapsed card. It
     belongs to the table inside, where a closed row hides it along with everything else.

     The padding is not conditional either — taking it away the instant a close begins pulled the
     table up while the row was still shrinking — and the table keeps its own height inside the clip,
     because a flex child of a shrinking box compresses instead. */
  .expansion-inner {
    overflow: hidden;
    min-height: 0;
  }

  /* The periods are a table of nine columns and the list is a row of five, and stacked flush
     against each other they read as one grid that has lost its mind. So the table is visibly
     inside the row: indented to where the name starts, on its own panel, with air above and below.
     Two systems, one plainly nested in the other. */
  .expansion-inner > * {
    min-height: max-content;
    margin: 0 ${DX.space[3]} ${DX.space[3]} ${DX.space[6]};
    padding: ${DX.space[3]} ${DX.space[4]} ${DX.space[2]};
    border: ${DX.hairline};
    border-radius: ${DX.radius.card};
    background: ${DX.surface};
  }

  /* Narrower lists: the name keeps its room; the rest steps back, least needed first. */
  header > .who {
    min-width: 160px;
  }

  @container projects (max-width: 1240px) {
    header > .due {
      flex-basis: 250px;
    }

    header > .dates {
      flex-basis: 150px;
    }

    header > .dates .created {
      display: none;
    }
  }

  @container projects (max-width: 1040px) {
    header > .dates {
      display: none;
    }
  }

  @container projects (max-width: 870px) {
    header > .assignee {
      display: none;
    }
  }

  @container projects (max-width: 710px) {
    header > .due {
      flex-basis: 210px;
    }

    header > .due em {
      display: none;
    }
  }

  @container projects (max-width: 650px) {
    header > .meta {
      display: none;
    }
  }
`;

/**
 * When it is due, as a chip.
 *
 * One shape for a filing date wherever it appears — on the project's row and in the queue — because
 * two shapes for one fact is two facts to learn. The outline stays neutral in every state: the dot
 * carries the colour, and a red box around a date turned the calendar into an alarm.
 */
/* Column names over the list, as quiet as the rail's captions; the row's own markup keeps them aligned. */
export const ListHead = styled(Card)`
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${DX.surface};

  header {
    min-height: 28px;
    padding-top: 0;
    padding-bottom: 0;
    cursor: default;
  }

  header:hover {
    background: none;
  }

  && header > * {
    ${groupLabel}
  }

  .pick {
    flex: 0 0 16px;
  }
`;

export const DueChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${DX.space[2]};
  padding: 1px ${DX.space[3]};
  border: ${DX.hairline};
  border-radius: ${DX.radius.control};
  font-size: ${DX.fz.sm};
  line-height: ${DX.lh.sm};
  color: ${DX.text};
  white-space: nowrap;

  .mark {
    flex: none;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${DX.tone.idle};
  }

  &[data-tone='warning'] .mark {
    background: ${DX.tone.warning};
  }

  &[data-tone='attention'] .mark {
    background: ${DX.tone.attention};
  }

  /* A period due on several dates at once — one per jurisdiction, or a filer category inherited
     from an earlier period. The chip leads with the first of them and the rest are a hover away,
     which nobody discovers unless the chip says there is something under it. */
  &[data-more='true'] {
    cursor: help;
    border-style: dashed;
  }

  /* The badge carries its own padding, so the chip stops paying for it twice. */
  &:has(.who) {
    padding-right: ${DX.space[1]};
  }

  /* The country code or the count, in a grey that can be read at 11px. */
  .who {
    padding: 0 4px;
    border-radius: 3px;
    background: ${DX.active};
    color: ${DX.text7};
    font-size: ${DX.fz.caption};
    line-height: 16px;
    letter-spacing: 0.04em;
  }
`;

/* ── What is waiting on you ──────────────────────────────────────────────────
   The list of projects answers "what have we got"; this answers "what do I do next", which is the
   question a specialist opens the app with. A row is a request rather than an object — the ask
   first, the project as its caption — and the grouping is by deadline, because that is the one
   thing about this work nobody controls. */
export const WorkList = styled.div`
  flex: 1;
  min-height: 0;
  padding: 0 ${DX.space[4]} ${DX.space[6]};
  overflow-y: auto;
  container: queue / inline-size;
`;

export const WorkGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${DX.space[2]};
  width: 100%;
  padding: ${DX.space[7]} ${DX.space[4]} ${DX.space[3]};
  border: none;
  background: none;
  cursor: pointer;
  ${groupLabel}

  .n {
    color: ${DX.textFaint};
  }

  .chev {
    color: ${DX.textFaint};
    transform: rotate(90deg);
    transition: transform 120ms ${DX.ease};
  }

  /* Closed, and saying how much is inside it: forty filings due next month are why nobody scrolls
     to the bottom of a queue, and they are still one click from being read. */
  &[data-closed='true'] .chev {
    transform: none;
  }

  &[data-tone='attention'] {
    color: ${DX.tone.attention};
  }
`;

/* The date a run of rows shares: periods arrive in waves, so the list names each one. */
export const WorkWave = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${DX.space[2]};
  padding: ${DX.space[3]} ${DX.space[4]} ${DX.space[1]};
  font-size: ${DX.fz.xs};
  line-height: ${DX.lh.xs};
  color: ${DX.text7};

  .n {
    color: ${DX.textFaint};
  }
`;

export const WorkRow = styled.div`
  /* One line: what to do, where, which period, when, and the button — and they stand together.
     A greedy first column pushed the date to the far right and left seven hundred pixels of nothing
     across the middle of every row; the ask takes what it needs up to a limit, and everything after
     it follows immediately, at the same place on every row. What is left over is the row's tail,
     which reads as margin rather than as a hole between two things. */
  display: flex;
  align-items: center;
  gap: ${DX.space[5]};
  min-height: 44px;
  padding: ${DX.space[3]} ${DX.space[4]};
  border-radius: ${DX.radius.item};
  cursor: pointer;

  > .ic {
    flex: none;
  }

  > .ask {
    flex: 1 1 auto;
    max-width: 520px;
  }

  > .ctx {
    flex: 0 0 150px;
  }

  > .due {
    flex: 0 0 190px;
  }

  > .act {
    flex: 0 0 80px;
  }

  /* Narrower queues: the task keeps its room, the period and the countdown step back. */
  > .ask {
    min-width: 160px;
  }

  @container queue (max-width: 770px) {
    > .ctx {
      display: none;
    }
  }

  @container queue (max-width: 600px) {
    > .due {
      flex-basis: 150px;
    }

    > .due em {
      display: none;
    }
  }

  /* No rule between rows. Twenty-five hairlines down a page of short sentences read as a texture —
     the eye counts the lines instead of the work. What separates two rows is the space between
     them, and what marks the one under the cursor is its own fill. */
  &:hover {
    background: ${DX.hover};
  }

  /* The glyph says which kind of work it is before the words do: a page of "Send for approval" and
     "Upload files" is read by shape long before it is read by sentence. Grey, all of it — the
     glyph is what the work is, not how urgent it is, and a column of blue tiles was colouring the
     one thing on the row that never changes. */
  .ic {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: ${DX.radius.control};
    background: ${DX.surfaceSunken};
    color: ${DX.textMuted};
  }

  .ask {
    min-width: 0;
    font-size: ${DX.fz.md};
    line-height: ${DX.lh.md};
    color: ${DX.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ask b {
    font-weight: ${DX.fw.medium};
  }

  /* The project is where the work is, not what the work is: it follows the ask, a shade quieter. */
  .ask .who {
    color: ${DX.textMuted};
  }

  .ctx {
    font-size: ${DX.fz.sm};
    line-height: ${DX.lh.sm};
    color: ${DX.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The chip the project's row wears, and beside it how far off that date is — the one thing a
     queue needs that a list does not. */
  .due {
    display: flex;
    align-items: center;
    gap: ${DX.space[2]};
    white-space: nowrap;
  }

  .due em {
    font-style: normal;
    font-size: ${DX.fz.xs};
    color: ${DX.textFaint};
  }

  /* The one red on the page, and it is six pixels of dot plus the words that say why. A missed
     filing date is not a late task — it is the thing this product exists to prevent — so it is
     marked plainly and once, rather than shouted in a row of red. */
  .due[data-tone='attention'] em {
    color: ${DX.tone.attention};
  }

  .act {
    display: flex;
    justify-content: flex-end;
  }

  /* Quiet until the row is pointed at: on a queue of nine, nine buttons in a column is a wall. And
     shorter than a toolbar control — a 32px button inside a 34px row was setting the row's height
     for everyone. */
  .act button {
    height: 24px;
    padding: 0 ${DX.space[3]};
    opacity: 0;
    transition: opacity ${DX.duration} ${DX.ease};
  }

  &:hover .act button,
  &:focus-within .act button {
    opacity: 1;
  }
`;

/* The list of cards. One column: a card is already a row, and two of them side by side would put
   the periods of one project next to the periods of another. */
export const CardList = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  container: projects / inline-size;
  /* Four pixels between rows, the same four the rail has: enough for the fill under the cursor to
     be a shape rather than a band, not enough to break the column. */
  gap: ${DX.space[1]};
  padding: ${DX.space[2]} ${DX.space[4]} ${DX.space[6]};
  overflow-y: auto;
`;

/* ── The overview ────────────────────────────────────────────────────────────
   Four facts above the queue, and a rule under them. Not a row of cards: cards
   would be the only boxes on a page built out of rules and air, and four boxes
   at the top of a list read as four buttons before they read as four numbers. */
export const QueueBand = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${DX.space[7]} 44px;
  padding: ${DX.space[6]} ${DX.space[4]} ${DX.space[6]};
  border-bottom: ${DX.hairline};
`;

export const QueueStat = styled.button.attrs({ type: 'button' })`
  display: block;
  min-width: 0;
  padding: ${DX.space[1]} ${DX.space[2]};
  margin: calc(-1 * ${DX.space[1]}) calc(-1 * ${DX.space[2]});
  border: none;
  border-radius: ${DX.radius.item};
  background: none;
  text-align: left;
  cursor: default;
  transition: background-color ${DX.duration} ${DX.ease};

  .k {
    display: block;
    ${DX.caption}
  }

  /* Large enough to be read before the words beside it, small enough not to shout: this page has
     nothing else above 14px, and a 24px figure would be the loudest thing on it. */
  .v {
    display: block;
    margin-top: ${DX.space[2]};
    font-size: 18px;
    font-weight: ${DX.fw.medium};
    line-height: 22px;
    color: ${DX.text};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .s {
    display: block;
    margin-top: 2px;
    font-size: ${DX.fz.xs};
    line-height: ${DX.lh.xs};
    color: ${DX.textFaint};
    white-space: nowrap;
  }

  /* A figure that is nothing to worry about is not coloured. Late filings are, and only they. */
  &[data-tone='attention'] .v {
    color: ${DX.tone.attention};
  }

  &[data-link='true'] {
    cursor: pointer;
  }

  &[data-link='true']:hover {
    background: ${DX.hover};
  }
`;

/* Where a workspace page lands when the rail opens it.
   The three of them are whole pages, written to sit under the app chrome with their own heading and
   their own margins; dropped into the canvas they keep both. This gives them the scroll and nothing
   else, so they are the page they always were — only with the rail still beside them. */
export const WorkspacePane = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;

  /* Settings arrives with "Projects / Settings" above it. Inside the rail that trail is both a
     repetition — the lit row already says where you are — and a way out of the redesign: the link
     goes to the classic list. The page keeps it at its own address; here the rail is the trail. */
  ${BreadcrumbContainer} {
    display: none;
  }
`;

/* ── Waiting for rows ────────────────────────────────────────────────────────
   The canvas used to say "Loading projects…" in twelve point grey and leave the rest of the page
   empty, so every filter click emptied the screen and refilled it. A skeleton holds the shape
   instead: same row height, same columns, same rhythm — because it IS the row, with grey where the
   words go, so the two can never drift apart. */
const fadeInSkeleton = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
`;

export const SkeletonBar = styled.span`
  display: block;
  height: 10px;
  max-width: 100%;
  border-radius: 3px;
  background: ${DX.surfaceSunken};
  animation: ${shimmer} 1400ms ${DX.ease} infinite;
`;

/* Nothing for the first breath. A filter that answers from cache should not flash a skeleton on its
   way past — the same reason the route spinner waits. */
export const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DX.space[1]};
  animation: ${fadeInSkeleton} 160ms ${DX.ease} 150ms backwards;
`;

/* The deadline card: an eyebrow, the range, then the dates as a timeline. */
export const Tip = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: rgba(255, 255, 255, 0.92);
  text-align: left;

  .eyebrow {
    font-size: 10px;
    line-height: 14px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.45);
  }

  /* Many countries: codes alone, wrapped. */
  .line[data-compact='true'] .who {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
  }

  .fof {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    padding-top: 9px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);

    b {
      font-weight: 500;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
  }

  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-top: 2px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    b {
      font-size: 15px;
      line-height: 20px;
      font-weight: 600;
    }
  }

  .status {
    color: rgba(255, 255, 255, 0.55);
    white-space: nowrap;
  }

  .line {
    list-style: none;
    margin: 10px 0 0;
    padding: 0;
  }

  .line li {
    position: relative;
    display: grid;
    grid-template-columns: 84px 1fr;
    column-gap: 8px;
    padding: 0 0 8px 16px;
  }

  .line li:last-child {
    padding-bottom: 0;
  }

  .line[data-long='true'] li {
    grid-template-columns: 112px 1fr;
  }

  /* The rail between one day's dot and the next. */
  .line li:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 13px;
    bottom: -3px;
    width: 1px;
    background: rgba(255, 255, 255, 0.14);
  }

  .dot {
    position: absolute;
    left: 0;
    top: 6px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
  }

  li[data-state='past'] .dot {
    background: transparent;
    box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, 0.35);
  }

  li[data-state='soon'] .dot {
    background: #e3b341;
  }

  li[data-state='ahead'] .dot {
    background: rgba(255, 255, 255, 0.45);
  }

  .when {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    em {
      font-style: normal;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.45);
    }
  }

  li[data-state='past'] .when {
    color: rgba(255, 255, 255, 0.62);
  }

  .who {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .country {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.72);
  }

  .country[data-current='true'] {
    color: #fff;
    font-weight: 500;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px;
    line-height: 14px;
    padding: 0 4px;
    border-radius: 3px;
    letter-spacing: 0.04em;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.65);
  }

  .country[data-current='true'] code {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
  }

  .note {
    margin-top: 10px;
    color: rgba(255, 255, 255, 0.5);
  }

  .note.first {
    margin-top: 4px;
  }
`;
